// NOTE: This file is stable and usually should not be modified.
// It is important that all functionality in this file is preserved, and should only be modified if explicitly requested.

import { ChevronDown, LogOut, UserIcon, UserPlus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.tsx';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog.tsx';
import { useLoggedInAccounts, type Account } from '@/hooks/useLoggedInAccounts';
import { genUserName } from '@/lib/genUserName';
import { clearAllMessages } from '@/lib/dmMessageStore';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface AccountSwitcherProps {
  onAddAccountClick: () => void;
}

export function AccountSwitcher({ onAddAccountClick }: AccountSwitcherProps) {
  const { currentUser, otherUsers, setLogin, removeLogin } = useLoggedInAccounts();
  const queryClient = useQueryClient();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [shouldClearOnLogout, setShouldClearOnLogout] = useState(false);

  if (!currentUser) return null;

  const getDisplayName = (account: Account): string => {
    return account.metadata.name ?? genUserName(account.pubkey);
  }

  const handleLogoutClick = () => {
    const shouldClear = localStorage.getItem('nostrion-clear-cache-on-logout') !== 'false';
    setShouldClearOnLogout(shouldClear);
    setShowLogoutConfirm(true);
  };

  const executeLogout = async () => {
    const shouldClear = shouldClearOnLogout;

    if (shouldClear) {
      try {
        await clearAllMessages();
        localStorage.removeItem('nwc-connections');
        localStorage.removeItem('nwc-active-connection');
        localStorage.removeItem('nostr:app-config');
      } catch (e) {
        console.warn('Could not clear some cache:', e);
      }
    }

    // Always clear the React Query cache so no decrypted data from the
    // previous user's session can be served to whoever logs in next.
    queryClient.clear();

    removeLogin(currentUser.id);

    // Small delay then reload for clean state reset
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button className='flex items-center gap-3 p-3 rounded-full hover:bg-accent transition-all w-full text-foreground'>
            <Avatar className='w-10 h-10'>
              <AvatarImage src={currentUser.metadata.picture} alt={getDisplayName(currentUser)} />
              <AvatarFallback>{getDisplayName(currentUser).charAt(0)}</AvatarFallback>
            </Avatar>
            <div className='flex-1 text-left hidden md:block truncate'>
              <p className='font-medium text-sm truncate'>{getDisplayName(currentUser)}</p>
            </div>
            <ChevronDown className='w-4 h-4 text-muted-foreground' />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className='w-56 p-2 animate-scale-in'>
          <div className='font-medium text-sm px-2 py-1.5'>Switch Account</div>
          {otherUsers.map((user) => (
            <DropdownMenuItem
              key={user.id}
              onClick={() => setLogin(user.id)}
              className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
            >
              <Avatar className='w-8 h-8'>
                <AvatarImage src={user.metadata.picture} alt={getDisplayName(user)} />
                <AvatarFallback>{getDisplayName(user)?.charAt(0) || <UserIcon />}</AvatarFallback>
              </Avatar>
              <div className='flex-1 truncate'>
                <p className='text-sm font-medium'>{getDisplayName(user)}</p>
              </div>
              {user.id === currentUser.id && <div className='w-2 h-2 rounded-full bg-primary'></div>}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onAddAccountClick}
            className='flex items-center gap-2 cursor-pointer p-2 rounded-md'
          >
            <UserPlus className='w-4 h-4' />
            <span>Add another account</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleLogoutClick}
            className='flex items-center gap-2 cursor-pointer p-2 rounded-md text-red-500'
          >
            <LogOut className='w-4 h-4' />
            <span>Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Log out?</AlertDialogTitle>
            <AlertDialogDescription>
              {shouldClearOnLogout
                ? 'Log out and clear cache/DMs for privacy? This will clear DM history and wallet connections.'
                : 'Log out of this account?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeLogout}
              className="bg-red-600 hover:bg-red-700"
            >
              Log out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
